import ApiService from "@/core/services/api.service";
import JwtService from "@/core/services/jwt.service";
import { saveCompanyId, saveUserId } from "../jwt.service";

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const UPDATE_PASSWORD = "updateUser";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_PASSWORD = "setPassword";
export const SET_ERROR = "setError";

const state = {
  isAuthenticated: !!JwtService.getToken(),
  isJobseekerAuthenticated: !!JwtService.getJobseekerToken(),
  registerStepOneData: {},
  canDo: [],
  userInfo: {},
  remainingDay: null,
  restrications: {},
  wallet: null,
};

const getters = {
  currentUser(state) {
    return state.userInfo;
  },
  userId(state) {
    return state.userInfo.userId;
  },
  companyId(state) {
    return state.userInfo.companyId;
  },
  userFullName(state) {
    return state.userInfo.fullName;
  },

  userImage(state) {
    return state.userInfo.img;
  },
  userGender(state) {
    return state.userInfo.genderId;
  },
  userPost(state) {
    return state.userInfo.post;
  },

  canDo(state) {
    return state.canDo;
  },

  userInfo(state) {
    return state.userInfo;
  },

  isAuthenticated(state) {
    return state.isAuthenticated;
  },

  isJobseekerAuthenticated(state) {
    return state.isJobseekerAuthenticated;
  },

  registerStepOneData(state) {
    return state.registerStepOneData;
  },

  remainingDay(state) {
    return state.remainingDay;
  },

  restrications(state) {
    return state.restrications;
  },

  wallet(state) {
    return state.wallet;
  },
};

const actions = {
  login(context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post("api/v2/login", {
        username: credentials.username.value,
        password: credentials.password.value,
        userTypeId: "2",
        recaptchaToken: credentials.recaptchaToken,
      })
        .then(({ data }) => {
          if (data.access_token) {
            const expiresIn =
              Math.floor(new Date().getTime() / 1000) + data.expires_in;
            context.commit("setAuth", {
              token: data.access_token,
              expiresIn: expiresIn,
              refreshToken: data.refresh_token,
            });
          }
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  jobseekerLogin(context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post("api/v2/jobseeker/login", {
        username: credentials.username.value,
        password: credentials.password.value,
        referrer: credentials.referrer,
        recaptchaToken: credentials.recaptchaToken,
      })
        .then(({ data }) => {
          if (data.access_token) {
            const expiresIn =
              Math.floor(new Date().getTime() / 1000) + data.expires_in;
            context.commit("setJobseekerAuth", {
              accessToken: data.access_token,
              expiresIn: expiresIn,
              refreshToken: data.refresh_token,
            });
          }
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  logout(context) {
    context.commit(PURGE_AUTH);
  },

  jobseekerLogout(context) {
    return new Promise((resolve) => {
      ApiService.post("api/v2/jobseeker/logout").then(({ data }) => {
        if (data.code == 230) {
          context.commit("purgeJobseekerAuth");
          context.commit("removeJobseekerResume");
          resolve(data);
        }
      });
    });
  },

  register(context, companyCredentials) {
    return new Promise((resolve, reject) => {
      ApiService.post("api/v2/users/", {
        name: companyCredentials.name.value,
        site: companyCredentials.website.value,
        numberEmployees: companyCredentials.numberOfEmployees.value,
        jobFieldId: companyCredentials.jobField.value,
        firstName: context.state.registerStepOneData.firstname.value,
        lastName: context.state.registerStepOneData.lastname.value,
        mobile: context.state.registerStepOneData.mobile.value,
        email: context.state.registerStepOneData.email.value,
        password: context.state.registerStepOneData.password.value,
        userId: context.state.registerStepOneData.userId,
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  jobseekerRegister(context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post("api/v2/jobseeker/register", {
        username: credentials.username.value,
        code: credentials.registerCode.value,
        password: credentials.password.value,
        referrer: credentials.referrer,
        resumeId: credentials.resumeId,
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  employerInfo(context) {
    return new Promise((resolve, reject) => {
      ApiService.setHeader("employer");
      ApiService.get("api/v2/employer/profile")
        .then(({ data }) => {
          context.commit("saveEmployerInfo", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  [VERIFY_AUTH](context) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("verify")
        .then(({ data }) => {
          context.commit(SET_AUTH, data);
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response.data.errors);
        });
    } else {
      context.commit(PURGE_AUTH);
    }
  },
  [UPDATE_PASSWORD](context, payload) {
    const password = payload;

    return ApiService.put("password", password).then(({ data }) => {
      context.commit(SET_PASSWORD, data);
      return data;
    });
  },
};

const mutations = {
  saveRegisterStepOneData(state, data) {
    state.registerStepOneData = data;
  },

  purgeRegistrationData(state) {
    state.registerStepOneData = {};
  },

  [SET_ERROR](state, error) {
    state.errors = error;
  },
  setAuth(state, data) {
    state.isAuthenticated = true;
    state.remainingDay = data.remainingDay;
    JwtService.saveToken(data);
  },

  setJobseekerAuth(state, data) {
    state.isJobseekerAuthenticated = true;
    JwtService.saveJobseekerToken(data);
  },

  changeUserImage(state, image) {
    state.userInfo.img = image;
  },

  saveEmployerInfo(state, data) {
    state.canDo = data.canSee;
    state.userInfo = data.user;
    state.userInfo.fullName = data.user.firstName + " " + data.user.lastName;
    state.remainingDay = data.remainingDaysOfActivePlan;
    state.wallet = data.wallet;
    state.restrications = {
      checklistCount: data.checklistCount,
      customTestCount: data.customTestCount,
      stepCount: data.stepCount,
      messagePatternCount: data.messagePatternCount,
      accessToPersonalityTest: data.accessToPersonalityTest,
      accessToRecruitmentRequest: data.accessToCreateRecruitmentRequest,
      roleCount: data.roleCount,
      scoringCardCount: data.scoringCardCount,
    };
    saveCompanyId(data.user.companyId);
    saveUserId(data.user.userId);
  },

  saveWallet(state, data) {
    state.wallet = data.wallet;
  },

  setRemainingDay(state, data) {
    state.remainingDay = data.remainingDay;
  },

  purgeJobseekerAuth(state) {
    state.isJobseekerAuthenticated = false;
    localStorage.removeItem("soha_jobseeker_token");
    localStorage.removeItem("soha_jobseeker_refresh_token");
  },

  [SET_PASSWORD](state, password) {
    state.user.password = password;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.canDo = [];
    state.userInfo = {};
    state.remainingDay = null;
    state.restrications = {};
    state.errors = {};
    state.wallet = null;
    JwtService.destroyToken();
    localStorage.clear();
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
